import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    AvailabilityDto,
    AvailabilityMessageType,
    ProductSettingsModel,
    WarehouseDto,
} from "@insite/client-framework/Types/ApiModels";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    productId: string;
    availability?: AvailabilityDto;
    warehouseResult?: WarehouseDto[] | undefined;
    unitOfMeasure: string;
    trackInventory: boolean;
    isProductDetailsPage?: boolean;
    failedToLoadInventory?: boolean;
    extendedStyles?: ProductAvailabilityStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ProductAvailabilityStyles {
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    availabilityByWarehouseLinkGridItem?: GridItemProps;
    inventoryMessage?: InjectableCss;
    messageText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    errorWrapper?: InjectableCss;
    errorText?: TypographyPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    availabilityByWarehouseWrapper?: InjectableCss;
    availabilityByWarehouseRow?: InjectableCss;
    availabilityByWarehouseCellLabel?: InjectableCss;
    availabilityByWarehouseCellQty?: InjectableCss;
    gridWrapper?: InjectableCss;
    availabilityZeroMessage?: InjectableCss;
    availabilityHescoMessage?: InjectableCss;
}

export const productAvailabilityStyles: ProductAvailabilityStyles = {
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
        `,
    },
    messageGridItem: {
        width: 12,
        css: css`
            color: #e96104;
            span {
                font-size: 14px;
            }
        `,
    },
    availabilityByWarehouseLinkGridItem: {
        width: 12,
    },
    centeringWrapper: {
        css: css`
            height: 100px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: 0 auto;
        `,
    },
    availabilityByWarehouseWrapper: {
        css: css``,
    },
    availabilityByWarehouseRow: {
        css: css`
            display: flex;
            flex-direction: row;
        `,
    },
    availabilityZeroMessage: {
        css: css`
            color: ${getColor("primary")};
        `,
    },
    availabilityByWarehouseCellLabel: {
        css: css`
            color: ${getColor("primary")};
            padding: 0;
            font-size: 14px;
            border: none;
            min-width: 153px;
            margin-right: 10px;
        `,
    },
    availabilityByWarehouseCellQty: {
        css: css`
            color: ${getColor("success")};
            padding: 0;
            font-size: 13px;
            border: none;
            font-weight: bold;
            span.zeero {
                color: #d6692b;
            }
        `,
    },
    messageText: {
        css: css`
            width: 100%;
            font-size: 15px;
            color: #d6692b;
        `,
    },
    realTimeText: {
        weight: "bold",
        css: css`
            display: inline-block;
            text-align: left;
            min-width: 25px;

            &::after {
                overflow: hidden;
                display: inline-block;
                vertical-align: bottom;
                animation: ellipsis steps(4, end) 900ms infinite 1s;
                content: "\\2026";
                width: 0;
            }

            @keyframes ellipsis {
                to {
                    width: 1.25em;
                }
            }
        `,
    },
    errorText: {
        weight: "bold",
    },
    gridWrapper: {
        css: css`
            border: ${getColor("common.border")} 1px solid;
            margin-top: 10px;
            border-bottom: none;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            margin-top: 30px;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
            div[class^="GridOffset"] {
                max-width: 100%;
                margin: 0;
                div[data-test-selector="availabilityMessageWrapper"] {
                    width: 100%;
                    [data-test-selector="availabilityByWarehouseModal"] {
                        width: 100%;
                    }
                    [data-test-selector="warehouseName"] {
                        border-right: ${getColor("common.border")} 1px solid;
                        border-bottom: ${getColor("common.border")} 1px solid;
                        width: 80%;
                        padding: 5px 10px;
                        font-weight: bold;
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(
                                theme,
                                [
                                    css`
                                        min-width: 153px;
                                        margin-right: 10px;
                                    `,
                                    css`
                                        min-width: 153px;
                                        margin-right: 10px;
                                    `,
                                    css`
                                        min-width: 153px;
                                        margin-right: 10px;
                                    `,
                                    css`
                                        min-width: 153px;
                                        margin-right: 10px;
                                    `,
                                    css`
                                        min-width: 153px;
                                        margin-right: 10px;
                                    `,
                                ],
                                "max",
                            )}
                    }
                    [data-test-selector="availabilityZeroMessage"] {
                        padding: 5px 10px;
                        font-weight: bold;
                        border-bottom: ${getColor("common.border")} 1px solid;
                    }
                    [data-test-selector="availabeQty"] {
                        border-bottom: ${getColor("common.border")} 1px solid;
                        width: 20%;
                        padding: 5px 10px;
                        text-align: center;
                    }
                }
                div[data-test-selector="availabilityGrid"] {
                    padding: 5px 10px;
                    border-bottom: ${getColor("common.border")} 1px solid;
                    width: 100%;
                    span[data-test-selector="availabilityMessage"] {
                        font-weight: bold;
                    }
                }
            }
        `,
    },
    availabilityHescoMessage: {
        css: css`
            width: 100%;
            font-size: 15px;
            color: #d6692b;
        `,
    },
};

const showLink = (
    availability: AvailabilityDto | undefined,
    trackInventory: boolean,
    productSettings: ProductSettingsModel,
    isProductDetailsPage?: boolean,
): boolean => {
    if (
        !availability ||
        availability.messageType === AvailabilityMessageType.NoMessage ||
        !trackInventory ||
        !productSettings.showInventoryAvailability ||
        !productSettings.displayInventoryPerWarehouse
    ) {
        return false;
    }

    if (!productSettings.displayInventoryPerWarehouseOnlyOnProductDetail) {
        return true;
    }

    return !!isProductDetailsPage;
};

const ProductAvailability = ({
    availability,
    warehouseResult,
    trackInventory,
    productSettings,
    isProductDetailsPage,
    failedToLoadInventory,
    extendedStyles,
}: Props) => {
    const [isLoading, setIsLoading] = useState(false);

    const [hescoMessage, setHescoMessage] = useState("");
    const [manufacturerMessage, setManufacturerMessage] = useState("");
    const [hescoAvailableMessage, setHescoAvailableMessage] = useState("");
    const [defaultMessage, setDefaultMessage] = useState(false);
    const [manufacturerRemoveMessage, setManufacturerRemoveMessage] = useState(false);

    const [styles] = useState(() => mergeToNew(productAvailabilityStyles, extendedStyles));

    if (failedToLoadInventory) {
        return (
            <StyledWrapper {...styles.errorWrapper}>
                <Typography {...styles.errorText}>{siteMessage("RealTimeInventory_InventoryLoadFailed ")}</Typography>
            </StyledWrapper>
        );
    }

    let inventoryTextComponent = <Typography {...styles.realTimeText} />;
    if (
        defaultMessage &&
        !manufacturerRemoveMessage &&
        availability &&
        availability.messageType !== AvailabilityMessageType.NoMessage &&
        availability.message
    ) {
        inventoryTextComponent = (
            <StyledWrapper data-test-selector="availabilityGrid" {...styles.inventoryMessage}>
                <Typography {...styles.messageText} data-test-selector="availabilityMessage">
                    {availability.message}
                </Typography>
            </StyledWrapper>
        );
    } else if (availability) {
        inventoryTextComponent = <></>;
    }

    const checkWarehouseQtyZero = (warehouseResult: WarehouseDto[] | undefined) => {
        if (warehouseResult !== undefined && warehouseResult.length >= 2) {
            if (warehouseResult[0].qty === 0 && warehouseResult[1].qty === 0) {
                setManufacturerMessage(siteMessage("CallForAvailability_Zero").toString());
                setDefaultMessage(false);
            } else {
                setHescoAvailableMessage(siteMessage("CallForAvailabilityHesco_Zero").toString());
                setDefaultMessage(true);
            }
            if (warehouseResult[0].qty > 0 && warehouseResult[1].qty === 0) {
                setManufacturerRemoveMessage(true);
            }
            if (warehouseResult[0].qty === 0 && warehouseResult[1].qty > 0) {
                setHescoAvailableMessage("");
            }
        } else if (warehouseResult !== undefined && warehouseResult.length > 0) {
            if (warehouseResult[0].description === "HESCO Availability" && warehouseResult[0].qty === 0) {
                setManufacturerMessage(siteMessage("CallForAvailability_Zero").toString());
                setDefaultMessage(false);
            }
            if (warehouseResult[0].description === "HESCO Availability" && warehouseResult[0].qty > 0) {
                setHescoAvailableMessage(siteMessage("CallForAvailabilityHesco_Zero").toString());
                setDefaultMessage(true);
                setManufacturerRemoveMessage(true);
            }
        } else {
            setHescoMessage("");
            setManufacturerMessage("");
        }
    };

    React.useEffect(() => {
        checkWarehouseQtyZero(warehouseResult);
    }, [warehouseResult]);

    return (
        <StyledWrapper
            {...(isProductDetailsPage && showLink(availability, trackInventory, productSettings)
                ? { ...styles.gridWrapper }
                : "")}
        >
            <GridContainer {...styles.container}>
                {showLink(availability, trackInventory, productSettings, isProductDetailsPage) && (
                    <>
                        <GridItem
                            data-test-selector="availabilityMessageWrapper"
                            {...styles.availabilityByWarehouseLinkGridItem}
                        >
                            {isLoading && (
                                <StyledWrapper {...styles.centeringWrapper}>
                                    <LoadingSpinner {...styles.spinner} />
                                </StyledWrapper>
                            )}
                            {!isLoading && (
                                <StyledWrapper
                                    {...styles.availabilityByWarehouseWrapper}
                                    data-test-selector="availabilityByWarehouseModal"
                                >
                                    {!defaultMessage ? (
                                        <>
                                            {hescoMessage !== undefined && hescoMessage !== "" ? (
                                                <StyledWrapper
                                                    {...styles.availabilityZeroMessage}
                                                    data-test-selector="availabilityZeroMessage"
                                                >
                                                    <span>{hescoMessage}</span>
                                                </StyledWrapper>
                                            ) : (
                                                <span></span>
                                            )}
                                            {manufacturerMessage !== undefined && manufacturerMessage !== "" ? (
                                                <StyledWrapper
                                                    {...styles.availabilityZeroMessage}
                                                    data-test-selector="availabilityZeroMessage"
                                                >
                                                    <span>{manufacturerMessage}</span>
                                                </StyledWrapper>
                                            ) : (
                                                <span></span>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            {warehouseResult?.slice(0, 2).map(warehouse => (
                                                <StyledWrapper
                                                    {...styles.availabilityByWarehouseRow}
                                                    key={warehouse.name}
                                                >
                                                    <StyledWrapper
                                                        data-test-selector="warehouseName"
                                                        {...styles.availabilityByWarehouseCellLabel}
                                                    >
                                                        {warehouse.description === "HESCO Availability" ? (
                                                            <span>{warehouse.description || warehouse.name}</span>
                                                        ) : (
                                                            <span>Manufacturer Availability</span>
                                                        )}
                                                    </StyledWrapper>
                                                    <StyledWrapper
                                                        data-test-selector="availabeQty"
                                                        {...styles.availabilityByWarehouseCellQty}
                                                    >
                                                        {warehouse.qty >= 1 ? (
                                                            <span>{warehouse.qty}</span>
                                                        ) : (
                                                            <span className="zeero">{warehouse.qty}</span>
                                                        )}
                                                    </StyledWrapper>
                                                </StyledWrapper>
                                            ))}
                                            {hescoAvailableMessage !== undefined && hescoAvailableMessage !== "" ? (
                                                <StyledWrapper
                                                    {...styles.availabilityHescoMessage}
                                                    data-test-selector="availabilityZeroMessage"
                                                >
                                                    <span>{hescoAvailableMessage}</span>
                                                </StyledWrapper>
                                            ) : (
                                                <span></span>
                                            )}
                                        </>
                                    )}
                                </StyledWrapper>
                            )}
                        </GridItem>
                    </>
                )}
                <GridItem {...styles.messageGridItem}>{inventoryTextComponent}</GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(ProductAvailability);
